import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Input,
  ListBox,
  ListBoxItem,
  SearchField,
} from 'react-aria-components';
import config from '@plone/registry';
import type { BlockEditProps } from '@plone/types';
import { useBlockTemplates } from '@plone-collective/volto-editortemplates/components/Providers/block-template-editor';
import type { TemplateData } from '@plone-collective/volto-editortemplates/types';
import { Loader } from '@plone-collective/volto-editortemplates/components/commons/loader';
import { getBlockIconAndName } from '@plone-collective/volto-editortemplates/helpers/helpers';
import { defineMessages, useIntl } from 'react-intl';
import { SearchIcon } from '@plone/components';
import './styles.css';

const messages = defineMessages({
  helpText: {
    id: 'helpText',
    defaultMessage:
      'Select a template from the list below to load it. You can search by template name to narrow results',
  },
  list: {
    id: 'list',
    defaultMessage: 'Available templates',
  },
  notemplates: {
    id: 'notemplates',
    defaultMessage: 'No templates found',
  },
  templateItem: {
    id: 'templateItem',
    defaultMessage: '{name}, block type: {type}.',
  },
});

type TemplateSelectorBlockProps = Pick<
  BlockEditProps,
  'onChangeBlock' | 'block' | 'data'
>;
const TemplateSelectorBlock = ({
  block,
  data,
  onChangeBlock,
}: TemplateSelectorBlockProps) => {
  const [liveMessage, setLiveMessage] = useState('');
  const { search, blockTemplates } = useBlockTemplates();
  const intl = useIntl();

  const handleTemplateSelect = useCallback(
    (selectedTemplate: TemplateData) => {
      onChangeBlock(block, { ...data, uid: selectedTemplate.uid });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const Container = config.getComponent('Container').component || 'div';
  const renderEmptyState = useCallback(() => {
    if (blockTemplates.loading) {
      return <Loader />;
    } else if (blockTemplates.error) {
      return (
        <div
          aria-label={intl.formatMessage({ id: 'Error' })}
          style={{ textAlign: 'center' }}
        >
          {intl.formatMessage({ id: 'Error' })}
        </div>
      );
    }
    return (
      <div
        aria-label={intl.formatMessage(messages.notemplates)}
        style={{ textAlign: 'center' }}
      >
        {intl.formatMessage(messages.notemplates)}
      </div>
    );
  }, [blockTemplates]);
  const handleSearch = (value: string) => {
    search(value);
  };
  useEffect(() => {
    const timeout = setTimeout(() => {
      setLiveMessage(
        `${intl.formatMessage(messages.list)}: ${blockTemplates?.templates?.length}`,
      );
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockTemplates?.templates]);

  return (
    <section className="block-template-selector">
      <Container id="template-selector-block">
        <div className="template-selector-block-helptext">
          {intl.formatMessage(messages.helpText)}
        </div>
        <SearchField
          onChange={handleSearch}
          className={'react-aria-SearchField search'}
        >
          <SearchIcon />
          <Input
            name="search"
            type="search"
            aria-label={`${intl.formatMessage({ id: 'Search' })}...`}
            placeholder={`${intl.formatMessage({ id: 'Search' })}...`}
            className={'react-aria-Input search-input'}
            autoComplete="off"
          />
          <Button aria-hidden="true" name="clear-search">
            x
          </Button>
        </SearchField>
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {liveMessage}
        </div>
        <ListBox
          aria-label={intl.formatMessage(messages.list)}
          layout="grid"
          selectionMode="single"
          renderEmptyState={renderEmptyState}
          className="react-aria-ListBox templates-list"
          shouldFocusWrap
          onSelectionChange={(keys) => {
            const selectedKey = Array.from(keys)[0];
            const selectedTemplate = blockTemplates.templates.find(
              (t) => t.uid === selectedKey,
            );
            if (selectedTemplate) handleTemplateSelect(selectedTemplate);
          }}
        >
          {blockTemplates?.templates?.map((template) => {
            const templateBlockType = Object.values(
              template?.config?.blocks,
            )?.[0]?.['@type'];
            const iconData = getBlockIconAndName(templateBlockType);

            return (
              <ListBoxItem
                key={template.uid}
                id={template.uid}
                className="react-aria-ListBoxItem templates-list-item"
                aria-label={intl.formatMessage(messages.templateItem, {
                  name: template.name,
                  type: iconData?.title,
                })}
              >
                <div className="template-item" aria-hidden="true">
                  <div className="template-item-icon">
                    <svg
                      {...iconData?.attributes}
                      dangerouslySetInnerHTML={{ __html: iconData?.content }}
                    />
                  </div>
                  <div className="template-item-content">
                    <div className="template-item-icon-title">
                      {iconData?.title}
                    </div>
                    <div className="template-item-name">{template.name}</div>
                  </div>
                </div>
              </ListBoxItem>
            );
          })}
        </ListBox>
      </Container>
    </section>
  );
};

export default TemplateSelectorBlock;
